import React from 'react';
import { BrowserRouter as Router, Redirect, Route } from 'react-router-dom';
import BuildingsIndex from '../components/pages/buildings/BuildingsIndex';
import TenantsIndex from '../components/pages/tenants/TenantsIndex';
import AuthIndex from '../components/pages/auth/AuthIndex';
import UsersIndex from '../components/pages/users/UsersIndex';
import LeasesIndex from '../components/pages/leases/LeasesIndex';
import PortfolioIndex from '../components/pages/portfolio/PortfolioIndex';
import NewsIndex from '../components/pages/news/NewsIndex';
import RouterBaseSwitch from './RouterBaseSwitch';
import AssetsIndex from '../components/pages/assets/AssetsIndex';
import { LayoutRouter } from './LayoutRouter';
import { ListDataPoints } from '../components/pages/tenants/ListDataPoints';
import { AuthRoles } from '../types/auth';
import { useNavigationManager } from '../navigation/navigationManager';
import { NavigableLayoutRouterSwitch } from './NavigableLayoutRouterSwitch';
import NotFoundPage from '../components/pages/NotFoundPage';
import EditProfile from '../components/pages/auth/EditProfile';
import DealsIndex from '../components/pages/deals/DealsIndex';
import { accessToken } from '../models/security';
import { ListAiPrompts } from '../components/pages/aiPrompts/ListAiPrompts';
import YardiSyncDashboard from '../components/pages/yardiSync/YardiSyncDashboard';
import YardiSyncRunDetail from '../components/pages/yardiSync/YardiSyncRunDetail';
import YardiSyncExceptions from '../components/pages/yardiSync/YardiSyncExceptions';
import YardiSyncAuditEvents from '../components/pages/yardiSync/YardiSyncAuditEvents';

export const RouteWrapper: React.FC = (): JSX.Element => {
  const {
    portfolioNavigator,
    buildingsNavigator,
    assetsNavigator,
    tenantsNavigator,
    leasesNavigator,
    usersNavigator,
    authNavigator,
    newsNavigator,
    dataPointsNavigator,
    dealsNavigator,
    aiPromptsNavigator,
    yardiNavigator,
  } = useNavigationManager();

  const navigableDisplayPaths = () => [
    portfolioNavigator.base().path,
    buildingsNavigator.base().path,
    assetsNavigator.base().path,
    tenantsNavigator.base().path,
    leasesNavigator.base().path,
    dealsNavigator.base().path,
    usersNavigator.base().path,
    newsNavigator.base().path,
    dataPointsNavigator.base().path,
    aiPromptsNavigator.base().path,
    yardiNavigator.base().path,
    `${authNavigator.base().path}/edit-profile`,
  ];

  return (

    <Router>
      <RouterBaseSwitch>
        {!accessToken.isCurrentRoleBroker()
          ? <Route exact path="/"><Redirect to={portfolioNavigator.base().path} /></Route>
          : <Route exact path="/"><Redirect to={assetsNavigator.base().path} /></Route>}
        <Route path={navigableDisplayPaths()}>
          <NavigableLayoutRouterSwitch>
            <Route path={portfolioNavigator.base().path} component={PortfolioIndex} />
            <Route path={buildingsNavigator.base().path} component={BuildingsIndex} />
            <Route path={assetsNavigator.base().path} component={AssetsIndex} />
            <Route path={tenantsNavigator.base().path} component={TenantsIndex} />
            <Route path={leasesNavigator.base().path} component={LeasesIndex} />
            <Route path={dealsNavigator.base().path} component={DealsIndex} />
            <Route path={usersNavigator.base().path} component={UsersIndex} />
            <Route path={newsNavigator.base().path} component={NewsIndex} />
            {accessToken.isCurrentRoleAdmin()
                && (
                <>
                  <LayoutRouter
                    exact
                    path={dataPointsNavigator.base().path}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={ListDataPoints}
                    isPrivate
                  />
                  <LayoutRouter
                    exact
                    path={aiPromptsNavigator.base().path}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={ListAiPrompts}
                    isPrivate
                  />
                  <LayoutRouter
                    exact
                    path={yardiNavigator.base().path}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={YardiSyncDashboard}
                    isPrivate
                  />
                  <LayoutRouter
                    exact
                    path={`${yardiNavigator.base().path}/runs/:id`}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={YardiSyncRunDetail}
                    isPrivate
                  />
                  <LayoutRouter
                    exact
                    path={`${yardiNavigator.base().path}/runs/:id/exceptions`}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={YardiSyncExceptions}
                    isPrivate
                  />
                  <LayoutRouter
                    exact
                    path={`${yardiNavigator.base().path}/runs/:id/audit-events`}
                    authorizedUsers={[AuthRoles.Admin]}
                    component={YardiSyncAuditEvents}
                    isPrivate
                  />
                </>
                )}
            <LayoutRouter
              exact
              path={`${authNavigator.base().path}/edit-profile`}
              component={EditProfile}
              authorizedUsers={[AuthRoles.Admin, AuthRoles.Broker, AuthRoles.User]}
              isPrivate
            />
          </NavigableLayoutRouterSwitch>
        </Route>
        <Route path={authNavigator.base().path} component={AuthIndex} />
        <Route
          path="*"
          render={() => <NotFoundPage />}
        />
      </RouterBaseSwitch>
    </Router>
  );
};
